import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../auth";
import SearchPatientModal from "../components/AgentPages/MyLeads/components/SearchPatientModal";
import AddNewContactModal from "../components/AgentPages/MyLeads/components/AddNewContactModal";
import { SEARCH_INPUT_ID } from "../components/common/UniversalSearchShortcutHandler/universalSearchShortcutUtils";
import type { ContactSearchPrefill } from "../utils/universalSearchPrefill";

interface UniversalSearchContextValue {
  isOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  focusSearchInput: () => void;
  openAddNewContact: (prefill?: ContactSearchPrefill) => void;
}

const UniversalSearchContext =
  createContext<UniversalSearchContextValue | null>(null);

export function UniversalSearchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isAddNewContactOpen, setIsAddNewContactOpen] = useState(false);
  const [addContactSearchPrefill, setAddContactSearchPrefill] = useState<
    ContactSearchPrefill | undefined
  >(undefined);
  const isOpenRef = useRef(isOpen);

  isOpenRef.current = isOpen;

  const openSearch = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
  }, []);

  const focusSearchInput = useCallback(() => {
    if (!isOpenRef.current) {
      setIsOpen(true);
      window.setTimeout(() => {
        const input = document.getElementById(SEARCH_INPUT_ID);
        input?.focus();
        if (input instanceof HTMLInputElement) {
          input.select();
        }
      }, 0);
      return;
    }

    const input = document.getElementById(SEARCH_INPUT_ID);
    input?.focus();
    if (input instanceof HTMLInputElement) {
      input.select();
    }
  }, []);

  const openAddNewContact = useCallback((prefill?: ContactSearchPrefill) => {
    setAddContactSearchPrefill(prefill);
    setIsAddNewContactOpen(true);
  }, []);

  const closeAddNewContact = useCallback(() => {
    setIsAddNewContactOpen(false);
    setAddContactSearchPrefill(undefined);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      openSearch,
      closeSearch,
      focusSearchInput,
      openAddNewContact,
    }),
    [isOpen, openSearch, closeSearch, focusSearchInput, openAddNewContact]
  );

  return (
    <UniversalSearchContext.Provider value={value}>
      {children}
      {isAuthenticated && (
        <>
          <SearchPatientModal
            open={isOpen}
            close={closeSearch}
            onRequestAddNewContact={openAddNewContact}
          />
          <AddNewContactModal
            open={isAddNewContactOpen}
            close={closeAddNewContact}
            searchPrefill={addContactSearchPrefill}
          />
        </>
      )}
    </UniversalSearchContext.Provider>
  );
}

export function useUniversalSearch(): UniversalSearchContextValue {
  const context = useContext(UniversalSearchContext);
  if (!context) {
    throw new Error(
      "useUniversalSearch must be used within UniversalSearchProvider"
    );
  }
  return context;
}

/** Safe hook for optional integration (e.g. header falling back to prop). */
export function useUniversalSearchOptional(): UniversalSearchContextValue | null {
  return useContext(UniversalSearchContext);
}
